<template>
  <div class="gms" @keydown.esc="open = false">
    <div class="gms__control" :class="{ 'is-open': open, 'is-disabled': disabled }" @click="onControlClick">
      <div class="gms__value" v-if="selectedOptions.length">
        {{ selectedOptions.map(o => o.abbr || o.label).join(', ') }}
      </div>
      <div v-else class="gms__placeholder">{{ placeholder }}</div>
      <span class="gms__arrow" aria-hidden="true" />
    </div>

    <!-- Обычный компактный dropdown -->
    <div
      v-if="open && !panelMode"
      class="gms__dropdown"
      ref="dropdown"
    >
      <div class="gms__search">
        <input
          v-model="q"
          class="gms__search-input"
          type="text"
          :placeholder="searchPlaceholder"
          aria-label="Поиск"
        />
      </div>

      <div class="gms__list" role="listbox" aria-multiselectable="true">
        <template v-if="itemsFiltered.length">
          <template v-for="(it, idx) in itemsFiltered" :key="it.__key || ('g-'+idx)">
            <div v-if="it.type==='group'" class="gms__group">{{ it.label }}</div>
            <label
              v-else
              class="gms__option"
              :class="{
                'gms__option--used': !modelValueSet.has(it.key) && usedKeysSet.has(it.key),
                'gms__option--selected': modelValueSet.has(it.key)
              }"
            >
              <input
                type="checkbox"
                class="gms__checkbox"
                :checked="modelValueSet.has(it.key)"
                @change="onToggle(it.key, $event.target.checked)"
              />
              <span class="gms__option-text">
                <span class="gms__abbr">{{ it.abbr || it.label }}</span>
                <span class="gms__label" v-if="showFullLabels"> — {{ it.label }}</span>
                <span v-if="it.count != null" class="gms__count">{{ it.count }}</span>
                <span v-if="!modelValueSet.has(it.key) && usedKeysSet.has(it.key)" class="gms__used-badge" title="Уже включён в другой наряд">уже в наряде</span>
              </span>
            </label>
          </template>
        </template>
        <div v-else class="gms__empty">Ничего не найдено</div>
      </div>

      <div class="gms__footer">
        <button class="gms__btn" type="button" @click="clear">Очистить</button>
        <button class="gms__btn gms__btn--primary" type="button" @click="applyClose">Готово</button>
      </div>
    </div>

    <!-- Полноэкранная боковая панель (для большого выбора личного состава) -->
    <teleport to="body" v-if="panelMode">
      <transition name="gms-panel-fade">
        <div v-if="open" class="gms-panel-overlay" @click.self="applyClose">
          <transition name="gms-panel-slide">
            <div v-if="open" class="gms-panel">
              <div class="gms-panel__header">
                <div class="gms-panel__title">
                  <h3>{{ panelTitle || placeholder }}</h3>
                  <div class="gms-panel__stats">
                    Выбрано: <b>{{ modelValueSet.size }}</b>
                    <span v-if="usedKeysSet.size"> · уже в других нарядах: <b>{{ usedKeysSet.size }}</b></span>
                  </div>
                </div>
                <button class="gms-panel__close" type="button" @click="applyClose" aria-label="Закрыть">✕</button>
              </div>

              <div class="gms-panel__search">
                <input
                  v-model="q"
                  class="gms-panel__search-input"
                  type="text"
                  :placeholder="searchPlaceholder"
                  aria-label="Поиск"
                />
              </div>

              <div class="gms-panel__list" role="listbox" aria-multiselectable="true">
                <template v-if="itemsFiltered.length">
                  <template v-for="(it, idx) in itemsFiltered" :key="it.__key || ('g-'+idx)">
                    <div v-if="it.type==='group'" class="gms-panel__group">{{ it.label }}</div>
                    <label
                      v-else
                      class="gms-panel__row"
                      :class="{
                        'is-selected': modelValueSet.has(it.key),
                        'is-used': !modelValueSet.has(it.key) && usedKeysSet.has(it.key),
                      }"
                    >
                      <input
                        type="checkbox"
                        class="gms-panel__check"
                        :checked="modelValueSet.has(it.key)"
                        @change="onToggle(it.key, $event.target.checked)"
                      />
                      <span class="gms-panel__abbr">{{ it.abbr || '№' }}</span>
                      <span class="gms-panel__name">{{ it.label }}</span>
                      <span v-if="!modelValueSet.has(it.key) && usedKeysSet.has(it.key)" class="gms-panel__badge">уже в наряде</span>
                      <span v-else-if="modelValueSet.has(it.key)" class="gms-panel__badge gms-panel__badge--ok">выбран</span>
                    </label>
                  </template>
                </template>
                <div v-else class="gms-panel__empty">Ничего не найдено</div>
              </div>

              <div class="gms-panel__footer">
                <button class="gms-panel__btn" type="button" @click="clear">Очистить всё</button>
                <div class="gms-panel__spacer" />
                <button class="gms-panel__btn gms-panel__btn--primary" type="button" @click="applyClose">Готово</button>
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
export default {
  name: 'GroupedMultiSelect',
  props: {
    options: { type: Array, required: true },
    modelValue: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Выберите…' },
    searchPlaceholder: { type: String, default: 'Поиск…' },
    showFullLabels: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    // Открывать как большую боковую панель на половину экрана
    panelMode: { type: Boolean, default: false },
    panelTitle: { type: String, default: '' },
    // Ключи опций, которые ранее уже выбраны (в других строках плана) — подсветятся
    usedKeys: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      open: false,
      q: '',
      internal: [],
    }
  },
  computed: {
    modelValueSet() { return new Set(this.internal.length ? this.internal : this.modelValue) },
    usedKeysSet() { return new Set(this.usedKeys || []) },
    
    normalizedOptions() {
      const out = []
      const pushOpt = (o) => out.push({ key: o.key, label: String(o.label || ''), abbr: o.abbr ? String(o.abbr) : undefined, count: typeof o.count === 'number' ? o.count : undefined })
      ;(this.options || []).forEach(entry => {
        if (entry && Array.isArray(entry.options)) {
          entry.options.forEach(pushOpt)
        } else if (entry && 'key' in entry) {
          pushOpt(entry)
        }
      })
      return out
    },
    
    items() {
      const out = []
      let k = 0
      const pushGroup = (label) => out.push({ type: 'group', label: String(label || ''), __key: 'g-' + (k++) })
      const pushOpt = (o) => out.push({ type: 'option', key: o.key, label: String(o.label || ''), abbr: o.abbr ? String(o.abbr) : undefined, count: typeof o.count === 'number' ? o.count : undefined, __key: 'o-' + (k++) })
      ;(this.options || []).forEach(entry => {
        if (entry && Array.isArray(entry.options)) {
          pushGroup(entry.label)
          entry.options.forEach(pushOpt)
        } else if (entry && 'key' in entry) {
          pushOpt(entry)
        }
      })
      return out
    },
    itemsFiltered() {
      const q = this.q.trim().toLowerCase()
      if (!q) return this.items
      const out = []
      let currentGroup = null, groupHasAny = false
      const flushGroup = () => { if (currentGroup && groupHasAny) out.push(currentGroup); currentGroup = null; groupHasAny = false }
      for (const it of this.items) {
        if (it.type === 'group') {
          flushGroup()
          currentGroup = { ...it }
          groupHasAny = false
        } else {
          const ok = (it.label && it.label.toLowerCase().includes(q)) || (it.abbr && it.abbr.toLowerCase().includes(q))
          if (ok) {
            if (currentGroup && !groupHasAny) { out.push(currentGroup); groupHasAny = true }
            out.push(it)
          }
        }
      }
      flushGroup()
      return out
    },
    selectedOptions() {
      const set = new Set(this.modelValue)
      return this.normalizedOptions.filter(o => set.has(o.key))
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(v) {
        if (!this.open) this.internal = Array.isArray(v) ? [...v] : []
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.onDocClick)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick)
  },
  methods: {
    onControlClick() {
      if (this.disabled) return
      this.open = !this.open
      if (this.open) {
        this.q = ''
        this.internal = [...(this.modelValue || [])]
      }
    },
    onToggle(key, checked) {
      const set = new Set(this.internal)
      if (checked) set.add(key)
      else set.delete(key)
      this.internal = [...set]
    },
    clear() {
      this.internal = []
      this.$emit('update:modelValue', [])
    },
    applyClose() {
      this.$emit('update:modelValue', [...this.internal])
      this.open = false
    },
    onDocClick(e) {
      if (!this.open) return
      // По ТЗ 03.09.2026: panelMode рендерит панель через <teleport to="body">,
      // поэтому клик по строке-чекбоксу физически ВНЕ this.$el — старая
      // проверка `!root.contains(e.target)` закрывала панель раньше, чем
      // сработает change чекбокса. Всегда игнорируем клики внутри
      // .gms-panel-overlay (и .gms-panel), а также overlay сам управляет
      // закрытием через @click.self="applyClose".
      if (e.target && e.target.closest && e.target.closest('.gms-panel-overlay, .gms-panel')) return
      const root = this.$el
      if (root && !root.contains(e.target)) this.applyClose()
    },
  }
}
</script>

<style scoped>
.gms { position: relative; width: 100%; }

.gms__control {
  min-height: 34px;
  padding: 8px 36px 8px 12px;
  border-radius: 10px;
  outline: 1px #2274D4 solid;
  outline-offset: -1px;
  background: #FFFFFF;
  display: flex;
  align-items: center;
  cursor: pointer;
}
.gms__control.is-open { box-shadow: 0 0 0 2px rgba(34,116,212,.2); }
.gms__control.is-disabled { cursor: not-allowed; opacity: .6; }

.gms__value { color: #111827; font-size: 14px; line-height: 22.4px; }
.gms__placeholder { color: #6B7280; font-size: 14px; }

.gms__arrow {
  margin-left: auto;
  width: 14px; height: 14px;
  background: #111827;
  clip-path: polygon(50% 70%, 0 0, 100% 0);
  opacity: .8;
}

.gms__dropdown {
  position: absolute;
  z-index: 20;
  top: calc(100% + 6px);
  left: 0;
  right: auto;
  width: 360px;
  max-width: none;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.08);
  display: flex;
  flex-direction: column;
  max-height: 560px;
}

.gms__search { padding: 8px; border-bottom: 1px solid #F1F2F4; flex: 0 0 auto; }
.gms__search-input {
  width: 100%; height: 34px;
  border: 1px solid #E5E7EB; border-radius: 8px;
  padding: 6px 10px; font-size: 13px; color: #111827;
  box-sizing: border-box;
}

.gms__list {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 0;
  max-height: 440px;
}

.gms__group { padding: 6px 8px; color: #6B7280; font-weight: 700; font-size: 12px; text-transform: none; }

.gms__option { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border-radius: 6px; }
.gms__option:hover { background: #F9FAFB; }
.gms__checkbox { width: 16px; height: 16px; }
.gms__option-text { display: inline-flex; align-items: center; gap: 6px; color: #111827; font-size: 14px; }
.gms__abbr { font-weight: 600; }
.gms__label { color: #6B7280; font-weight: 500; }
.gms__count { margin-left: 6px; color: #374151; font-weight: 700; font-size: 12px; background: #F3F4F6; border-radius: 6px; padding: 1px 6px; }

.gms__empty { padding: 12px; color: #6B7280; font-size: 13px; }

.gms__footer { display: flex; justify-content: space-between; padding: 8px; border-top: 1px solid #F1F2F4; flex: 0 0 auto; background: #fff; border-radius: 0 0 10px 10px; }
.gms__btn {
  height: 32px; padding: 0 12px; border-radius: 8px; border: 1px solid #E5E7EB; background: #FFFFFF; cursor: pointer;
  font-weight: 600; font-size: 13px; color: #111827;
}
.gms__btn--primary { background: #2A8A1B; color: #fff; border-color: #2A8A1B; }

/* Подсветка уже занятых опций в обычном dropdown */
.gms__option--used { background: #FEF3C7; border-left: 3px solid #F59E0B; padding-left: 5px; }
.gms__option--selected { background: #DBEAFE; }
.gms__used-badge { margin-left: auto; padding: 1px 6px; background: #F59E0B; color: #fff; font-size: 10px; font-weight: 700; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.3px; }

/* ================ БОКОВАЯ ПАНЕЛЬ (panelMode) ================ */
.gms-panel-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 9998;
  display: flex;
  justify-content: flex-end;
}
.gms-panel {
  width: 50vw;
  min-width: 480px;
  max-width: 780px;
  height: 100vh;
  background: #fff;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  z-index: 9999;
}
.gms-panel__header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px 16px;
  border-bottom: 2px solid #374151;
  background: #F9FAFB;
}
.gms-panel__title { flex: 1 1 auto; min-width: 0; }
.gms-panel__title h3 { margin: 0 0 4px; font-size: 20px; font-weight: 800; color: #111827; }
.gms-panel__stats { font-size: 14px; color: #4B5563; }
.gms-panel__stats b { color: #111827; }
.gms-panel__close {
  width: 36px; height: 36px; border: 0; background: #F3F4F6; border-radius: 8px;
  font-size: 20px; cursor: pointer; color: #374151;
}
.gms-panel__close:hover { background: #E5E7EB; }

.gms-panel__search { padding: 12px 20px; border-bottom: 1px solid #E5E7EB; }
.gms-panel__search-input {
  width: 100%; height: 44px; padding: 0 16px;
  border: 2px solid #D1D5DB; border-radius: 10px;
  font-size: 16px; color: #111827; background: #fff;
  outline: none; font-family: inherit;
}
.gms-panel__search-input:focus { border-color: #2F78EE; box-shadow: 0 0 0 3px rgba(47, 120, 238, 0.15); }

.gms-panel__list {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 8px 12px 12px;
}
.gms-panel__group {
  padding: 12px 8px 6px;
  color: #6B7280;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  border-bottom: 1px solid #F3F4F6;
  margin-bottom: 4px;
}
.gms-panel__row {
  display: grid;
  grid-template-columns: 24px 70px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  border: 2px solid transparent;
  margin-bottom: 4px;
}
.gms-panel__row:hover { background: #F9FAFB; }
.gms-panel__row.is-used {
  background: #FEF3C7;
  border-color: #F59E0B;
}
.gms-panel__row.is-selected {
  background: #DBEAFE;
  border-color: #2F78EE;
}
.gms-panel__check { width: 20px; height: 20px; accent-color: #2F78EE; cursor: pointer; }
.gms-panel__abbr {
  padding: 4px 8px;
  background: #F3F4F6;
  border-radius: 6px;
  font-weight: 700;
  color: #374151;
  text-align: center;
  font-size: 14px;
  font-family: 'Menlo', monospace;
}
.gms-panel__row.is-selected .gms-panel__abbr { background: #2F78EE; color: #fff; }
.gms-panel__row.is-used .gms-panel__abbr { background: #F59E0B; color: #fff; }
.gms-panel__name { color: #111827; font-size: 15px; font-weight: 500; }
.gms-panel__badge {
  padding: 3px 10px;
  background: #F59E0B;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.gms-panel__badge--ok { background: #2F78EE; }
.gms-panel__empty { padding: 40px; text-align: center; color: #9CA3AF; }

.gms-panel__footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-top: 2px solid #E5E7EB;
  background: #F9FAFB;
}
.gms-panel__spacer { flex: 1 1 auto; }
.gms-panel__btn {
  height: 44px; padding: 0 24px; border-radius: 10px; border: 1px solid #D1D5DB;
  background: #fff; cursor: pointer; font-weight: 700; font-size: 15px; color: #374151;
  font-family: inherit;
}
.gms-panel__btn:hover { background: #F3F4F6; }
.gms-panel__btn--primary { background: #2F78EE; color: #fff; border-color: #2F78EE; }
.gms-panel__btn--primary:hover { background: #1D4ED8; }

/* Transitions */
.gms-panel-fade-enter-active,
.gms-panel-fade-leave-active { transition: opacity 0.2s ease; }
.gms-panel-fade-enter-from,
.gms-panel-fade-leave-to { opacity: 0; }

.gms-panel-slide-enter-active,
.gms-panel-slide-leave-active { transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1); }
.gms-panel-slide-enter-from,
.gms-panel-slide-leave-to { transform: translateX(100%); }
</style>
